import { useMemo, useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import {
  Alert,
  Badge,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import type {
  YukumlulukKalem,
  YukumlulukKalemGuncelle,
  YukumlulukKanitTipi,
} from '@isg/validation';
import {
  useYukumlulukGizle,
  useYukumlulukKalemAc,
  useYukumlulukKalemGuncelle,
  useYukumlulukKalemleri,
  useKapilar,
} from '../api/queries';
import { BosListeNotu, QueryStatus } from './parts';
import { Rozet, Sayfalama, SolukSatir, SuzgecGostergesi, KapiSebebi } from '../sozlesme';
import { ANLAM } from '../theme';
import {
  YUKUMLULUK_KANIT_TIPI_ETIKETI,
  YUKUMLULUK_KAYNAK_ETIKETI,
  YUKUMLULUK_TIP_ETIKETI,
} from './yukumluluk-parts';

/**
 * ✏️ **TUR 8 / C2 + C3 — YÜKÜMLÜLÜK KÜTÜPHANESİ: KURAL YÜZEYİ.**
 *
 * > **KÜTÜPHANE ÖNERİR, MÜŞTERİ BELİRLER** (§19/9'un yükümlülük kolu). Bu
 * > ekran o cümlenin **yolu**dur.
 *
 * ⚠️ **BU EKRAN ON GÜNLÜK BİR 500'ÜN ÜSTÜNE AÇILDI.** `GET /api/obligations`
 * 26.08.2026'da yazıldı ve **hiçbir ekran onu çağırmadı**; şema bir kolonu
 * tanımadığı için uç ilk günden beri patlıyordu ve **kimse görmedi.**
 * *"Uç var" cümlesi "yol var" cümlesi değildir* (Yönerge §6/7 — h-3'ün
 * `POST lookup/:listeKodu` vakasının kardeşi).
 *
 * ⚠️ **KİMLİK KİLİDİ EKRANDA DA GÖRÜNÜR** (§19/9): kütüphane nüshasında `ad`
 * ve `dayanak` **salt okunurdur** — alanı açıp 422 aldırmak §2.40'ın en kötü
 * seçeneğidir (*"basılınca hata verir"*). Müşterinin **kendi** kaleminde
 * (ref'i olmayan satır) ikisi de serbesttir ve ekran bunu **alanın
 * düzenlenebilirliğiyle** söyler.
 *
 * ⚠️ **GİZLEME UZMANA KAPALI — "ŞAPKA KİLİDİ AÇMAZ."** Anahtar yalnız holding
 * yöneticisine çizilir; uzman **kuralı** düzenler, kalemi **kullanımdan
 * kaldırmaz** (§19/10 kararının birebir aynısı).
 *
 * ⚠️ **EKRAN İKİNCİ KURAL YAZMAZ** (§2.16): tip ↔ alan tutarlılığı sunucuda
 * `yukumlulukKalemIhlalleri`ndedir ve **422** üretir. Buradaki tek iş, o
 * kuralın anlamsız olduğu alanı **hiç çizmemektir** (periyot alanları yalnız
 * periyodik kalemde).
 */
const SAYFA_BOYU = 25;

export function YukumlulukKutuphaneScreen(): React.JSX.Element {
  const kalemler = useYukumlulukKalemleri();
  const guncelle = useYukumlulukKalemGuncelle();
  const gizle = useYukumlulukGizle();
  const ac = useYukumlulukKalemAc();

  const [kaynakSuzgeci, setKaynakSuzgeci] = useState<string>('tumu');
  const [gizliGoster, setGizliGoster] = useState(false);
  const [sayfa, setSayfa] = useState(1);
  const [taslak, setTaslak] = useState<Taslak>(BOS_TASLAK);
  // P13-b (madde 225): iki kapı SUNUCUDAN — kural düzenleme holding + uzman (§19/10) ·
  // gizleme yalnız holding yön.; ekran şapka dizgesi yazmaz.
  const kapilar = useKapilar();
  const gizleyebilir = kapilar.data?.yukumlulukGizlemeKapisi.yazabilir ?? false;
  const duzenleyebilir = kapilar.data?.yukumlulukKutuphaneKapisi.yazabilir ?? false;

  const satirlar = useMemo(() => {
    const hepsi = kalemler.data ?? [];
    return hepsi.filter(
      (k) => (gizliGoster || !k.gizli) && (kaynakSuzgeci === 'tumu' || k.kaynak === kaynakSuzgeci),
    );
  }, [kalemler.data, kaynakSuzgeci, gizliGoster]);
  const sayfadakiler = satirlar.slice((sayfa - 1) * SAYFA_BOYU, sayfa * SAYFA_BOYU);
  const etkinSuzgecSayisi = (kaynakSuzgeci === 'tumu' ? 0 : 1) + (gizliGoster ? 1 : 0);

  const dokunuldu = taslak.ad.trim().length > 0;
  // P12 (madde 224) — kaydedilmemiş kütüphane kalemi çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'kütüphane kalemi');
  const kaydedilebilir = dokunuldu && (taslak.tip === 'periyodik' || taslak.altSekil !== null);

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Title order={4}>Yükümlülük kütüphanesi</Title>
        <Text size="sm" c="dimmed" data-testid="yukumluluk-kalem-sayisi">
          {satirlar.length} kalem
        </Text>
      </Group>

      <Group gap="sm" align="flex-end">
        <Select
          size="xs"
          label="Kaynak"
          data={[
            { value: 'tumu', label: 'Tümü' },
            { value: 'mevzuat', label: YUKUMLULUK_KAYNAK_ETIKETI.mevzuat! },
            { value: 'ic_prosedur', label: YUKUMLULUK_KAYNAK_ETIKETI.ic_prosedur! },
          ]}
          value={kaynakSuzgeci}
          onChange={(v) => {
            setKaynakSuzgeci(v ?? 'tumu');
            setSayfa(1);
          }}
          data-testid="kutuphane-kaynak-suzgeci"
          w={200}
        />
        <Switch
          size="xs"
          label="Gizlenenleri göster"
          checked={gizliGoster}
          data-testid="kutuphane-gizli-suzgeci"
          onChange={(e) => {
            setGizliGoster(e.currentTarget.checked);
            setSayfa(1);
          }}
        />
      </Group>

      <SuzgecGostergesi
        etkinSayisi={etkinSuzgecSayisi}
        onTemizle={() => {
          setKaynakSuzgeci('tumu');
          setGizliGoster(false);
          setSayfa(1);
        }}
      />

      <QueryStatus queries={[kalemler]} />
      {kapilar.data && (
        <KapiSebebi
          durum={kapilar.data.yukumlulukKutuphaneKapisi}
          nesne="Kütüphane kalemini"
          fiil="düzenler"
          testId="kutuphane-yazamama-sebebi"
        />
      )}
      {kapilar.data && (
        <KapiSebebi
          durum={kapilar.data.yukumlulukGizlemeKapisi}
          nesne="Kütüphane kalemini"
          fiil="gizler"
          testId="kutuphane-gizleme-yazamama-sebebi"
        />
      )}

      {kalemler.data && satirlar.length === 0 && <BosListeNotu mesaj="Bu süzgeçle kalem yok." />}

      {kalemler.data && (
        <Table striped highlightOnHover data-testid="kutuphane-tablosu">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Yükümlülük</Table.Th>
              <Table.Th>Kaynak</Table.Th>
              <Table.Th>Tip</Table.Th>
              <Table.Th>Periyot (ay)</Table.Th>
              <Table.Th>Çalışan eşiği</Table.Th>
              <Table.Th>Kanıt</Table.Th>
              <Table.Th>Kullanımda</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sayfadakiler.map((kalem) => (
              <KalemSatiri
                key={kalem.id}
                kalem={kalem}
                duzenleyebilir={duzenleyebilir}
                gizleyebilir={gizleyebilir}
                calisiyor={guncelle.isPending || gizle.isPending}
                onGuncelle={(yama) => guncelle.mutate({ id: kalem.id, yama })}
                onGizle={(deger) => gizle.mutate({ id: kalem.id, gizli: deger })}
              />
            ))}

            {/* P1-a — SOLUK SATIR: C3'ün yolu. "Ekle" düğmesi de "henüz kayıt
                yok" kutusu da gereksizdir; satır her zaman listenin SONUNDADIR. */}
            {duzenleyebilir && (
              <SolukSatir
                dokunuldu={dokunuldu}
                kaydedilebilir={kaydedilebilir}
                kaydediliyor={ac.isPending}
                kolonSayisi={6}
                testId="kutuphane-soluk-satir"
                onKaydet={() => {
                  ac.mutate(
                    {
                      ad: taslak.ad.trim(),
                      dayanak: taslak.dayanak.trim() || null,
                      tip: taslak.tip,
                      altSekil: taslak.tip === 'surekli' ? taslak.altSekil : null,
                      periyotBicim: taslak.tip === 'periyodik' ? 'sabit' : null,
                      periyotAy: taslak.tip === 'periyodik' ? taslak.periyotAy : null,
                      kanitTipi: taslak.kanitTipi,
                    },
                    { onSuccess: () => setTaslak(BOS_TASLAK) },
                  );
                }}
              >
                <Table.Td>
                  <TextInput
                    size="xs"
                    placeholder="Kendi yükümlülüğünüz"
                    value={taslak.ad}
                    data-testid="kutuphane-yeni-ad"
                    // ⚠️ **DEĞER GÜNCELLEYİCİNİN İÇİNDE OKUNAMAZ.** React,
                    // olay gönderimi bitince `currentTarget`ı NULL'lar;
                    // fonksiyonel güncelleyici (`(o) => ...`) daha SONRA
                    // koşar ve o an okumak `TypeError`la **satırı komple
                    // düşürür** — kullanıcı yazdığını kaybeder ve ekranda
                    // hiçbir hata görmez (§2.40'ın en sessiz kolu; bu tam
                    // olarak yol testinde yakalandı). Değer ÖNCE okunur.
                    onChange={(e) => {
                      const deger = e.currentTarget.value;
                      setTaslak((o) => ({ ...o, ad: deger }));
                    }}
                  />
                </Table.Td>
                <Table.Td>
                  {/* ⚠️ KAYNAK SEÇTİRİLMEZ — SUNUCU `ic_prosedur` YAZAR.
                      "Mevzuat" etiketi ÜRÜNÜN verdiği kalemlerin işaretidir;
                      seçtirmek, kütüphanenin "kimin sözü" bilgisini yok
                      ederdi. Ekran bunu bir açılır listeyle değil, DEĞİŞMEZ
                      bir rozetle söyler. */}
                  <Rozet anlam="bilgi" testId="kutuphane-yeni-kaynak">
                    {YUKUMLULUK_KAYNAK_ETIKETI.ic_prosedur!}
                  </Rozet>
                </Table.Td>
                <Table.Td>
                  <Stack gap={4}>
                    <Select
                      size="xs"
                      comboboxProps={{ withinPortal: true }}
                      data={[
                        { value: 'periyodik', label: YUKUMLULUK_TIP_ETIKETI.periyodik! },
                        { value: 'surekli', label: YUKUMLULUK_TIP_ETIKETI.surekli! },
                      ]}
                      value={taslak.tip}
                      data-testid="kutuphane-yeni-tip"
                      onChange={(v) =>
                        setTaslak((o) => ({ ...o, tip: (v ?? 'periyodik') as Taslak['tip'] }))
                      }
                    />
                    {/* ⚠️ SÜREKLİ KALEM ALT ŞEKİL TAŞIR, PERİYODİK TAŞIMAZ:
                        alan yalnız anlamlı olduğu tipte ÇİZİLİR (§2.40 —
                        çalışamayacak kontrol görünmez olur). */}
                    {taslak.tip === 'surekli' && (
                      <Select
                        size="xs"
                        placeholder="Alt şekil"
                        comboboxProps={{ withinPortal: true }}
                        data={[
                          { value: 'sayi', label: 'Sayı' },
                          { value: 'varlik', label: 'Var / yok' },
                          { value: 'sure', label: 'Süre' },
                        ]}
                        value={taslak.altSekil}
                        data-testid="kutuphane-yeni-alt-sekil"
                        onChange={(v) =>
                          setTaslak((o) => ({ ...o, altSekil: v as Taslak['altSekil'] }))
                        }
                      />
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  {taslak.tip === 'periyodik' ? (
                    <NumberInput
                      size="xs"
                      min={1}
                      placeholder="ay"
                      value={taslak.periyotAy ?? ''}
                      data-testid="kutuphane-yeni-periyot"
                      onChange={(v) =>
                        setTaslak((o) => ({ ...o, periyotAy: typeof v === 'number' ? v : null }))
                      }
                    />
                  ) : (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="xs"
                    placeholder="Dayanak"
                    value={taslak.dayanak}
                    data-testid="kutuphane-yeni-dayanak"
                    onChange={(e) => {
                      const deger = e.currentTarget.value;
                      setTaslak((o) => ({ ...o, dayanak: deger }));
                    }}
                  />
                </Table.Td>
                <Table.Td>
                  <KanitSecici
                    deger={taslak.kanitTipi}
                    testId="kutuphane-yeni-kanit"
                    onDegis={(v) => setTaslak((o) => ({ ...o, kanitTipi: v }))}
                  />
                </Table.Td>
                {/* ⚠️ SON KOLON YOK: `SolukSatir` kaydet hücresini KENDİ ekler
                    (`kolonSayisi` yalnız ikinci katmanın colSpan'i içindir).
                    Buraya boş bir hücre koymak tabloyu bir kolon kaydırırdı. */}
              </SolukSatir>
            )}
          </Table.Tbody>
        </Table>
      )}

      <Sayfalama
        sayfa={sayfa}
        sayfaBoyutu={SAYFA_BOYU}
        toplam={satirlar.length}
        onDegis={setSayfa}
      />

      {/* ⚠️ BU NOT, TAKVİMDEKİ SORUMLULUK NOTUNUN KÜTÜPHANE KOLUDUR: burada
          değiştirilen kural takvimin TAMAMINI etkiler ve kullanıcı bunu
          basmadan ÖNCE bilmelidir (§2.40). */}
      <Alert color={ANLAM.bilgi} variant="light" data-testid="kutuphane-notu">
        <Text size="sm">
          Buradaki kurallar <b>tüm işyerlerinin</b> takvimini belirler. Ürün kalemlerinde adı ve
          dayanağı değiştiremezsiniz; periyot, eşik ve kanıt beklentisi sizindir. Kendi
          yükümlülüğünüzü en alttaki satırdan ekleyebilirsiniz.
        </Text>
      </Alert>
    </Stack>
  );
}

interface Taslak {
  ad: string;
  dayanak: string;
  tip: 'periyodik' | 'surekli';
  altSekil: 'sayi' | 'varlik' | 'sure' | null;
  periyotAy: number | null;
  kanitTipi: YukumlulukKanitTipi | null;
}

const BOS_TASLAK: Taslak = {
  ad: '',
  dayanak: '',
  tip: 'periyodik',
  altSekil: null,
  periyotAy: 12,
  kanitTipi: null,
};

/**
 * ⚠️ **DÜZENLEME SATIR İÇİNDEDİR (P1)** ve **alan bazlıdır:** her alan kendi
 * değişikliğini kaydeder. Ayrı bir *"düzenle → kaydet"* kipi, yirmi satırlık
 * bir kural taramasında ekranı yirmi kez kip değiştirmeye zorlardı.
 */
function KalemSatiri({
  kalem,
  duzenleyebilir,
  gizleyebilir,
  calisiyor,
  onGuncelle,
  onGizle,
}: {
  kalem: YukumlulukKalem;
  duzenleyebilir: boolean;
  gizleyebilir: boolean;
  calisiyor: boolean;
  onGuncelle: (yama: YukumlulukKalemGuncelle) => void;
  onGizle: (deger: boolean) => void;
}): React.JSX.Element {
  // ⚠️ KİMLİK KİLİDİ: ürün kaleminde ad/dayanak SALT OKUNUR (§19/9).
  const urunKalemi = kalem.kutuphaneRef !== null;
  return (
    <Table.Tr data-testid="kutuphane-satiri" data-gizli={kalem.gizli ? 'evet' : 'hayir'}>
      <Table.Td>
        <Text size="sm" td={kalem.gizli ? 'line-through' : undefined}>
          {kalem.ad}
        </Text>
        {kalem.dayanak && (
          <Text size="xs" c="dimmed">
            {kalem.dayanak}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm">{YUKUMLULUK_KAYNAK_ETIKETI[kalem.kaynak]}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{YUKUMLULUK_TIP_ETIKETI[kalem.tip]}</Text>
      </Table.Td>
      <Table.Td>
        {/* Periyot yalnız PERİYODİK kalemde anlamlıdır; `tehlike_sinifi`
            biçiminde üç değer vardır ve tek kutuya sığmaz — o satır salt
            okunur gösterilir (ikinci taşıyıcı açmamak için: 91-1 dersi). */}
        {kalem.tip !== 'periyodik' ? (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ) : kalem.periyotBicim === 'tehlike_sinifi' ? (
          <Tooltip label="Tehlike sınıfına göre değişir: az / tehlikeli / çok">
            <Text size="sm" data-testid="kutuphane-uclu-periyot">
              {kalem.periyotAyAz} / {kalem.periyotAyTehlikeli} / {kalem.periyotAyCok}
            </Text>
          </Tooltip>
        ) : (
          <NumberInput
            size="xs"
            min={1}
            w={90}
            placeholder="—"
            disabled={!duzenleyebilir || calisiyor}
            value={kalem.periyotAy ?? ''}
            data-testid="kutuphane-periyot"
            onBlur={(e) => {
              const ham = e.target.value;
              const deger = ham === '' ? null : Number(ham);
              if (deger === kalem.periyotAy) return;
              // ⚠️ `bos` biçim İ8'in taşıyıcısıdır: periyodu silinen kalem
              // SESSİZ BEKLER, "0 ay" gibi anlamsız bir değere düşmez.
              onGuncelle({ periyotAy: deger, periyotBicim: deger === null ? 'bos' : 'sabit' });
            }}
          />
        )}
      </Table.Td>
      <Table.Td>
        <NumberInput
          size="xs"
          min={1}
          w={90}
          placeholder="—"
          disabled={!duzenleyebilir || calisiyor}
          value={kalem.calisanSayisiEsigi ?? ''}
          data-testid="kutuphane-esik"
          onBlur={(e) => {
            const ham = e.target.value;
            const deger = ham === '' ? null : Number(ham);
            if (deger !== kalem.calisanSayisiEsigi) onGuncelle({ calisanSayisiEsigi: deger });
          }}
        />
      </Table.Td>
      <Table.Td>
        <Stack gap={2}>
          <KanitSecici
            deger={kalem.kanitTipi}
            devredisi={!duzenleyebilir || calisiyor}
            testId="kutuphane-kanit"
            onDegis={(v) => onGuncelle({ kanitTipi: v })}
          />
          <TextInput
            size="xs"
            placeholder="Kanıt notu"
            disabled={!duzenleyebilir || calisiyor}
            defaultValue={kalem.kanitMetni ?? ''}
            data-testid="kutuphane-kanit-metni"
            onBlur={(e) => {
              const deger = e.target.value.trim() || null;
              if (deger !== kalem.kanitMetni) onGuncelle({ kanitMetni: deger });
            }}
          />
        </Stack>
      </Table.Td>
      <Table.Td>
        {/* ⚠️ NÜSHA SİLİNMEZ, GİZLENİR — ve anahtar YALNIZ holding
            yöneticisine çizilir ("şapka kilidi açmaz"): uzman kuralı
            düzenler, kalemi kullanımdan kaldırmaz. */}
        {gizleyebilir ? (
          <Switch
            size="xs"
            checked={!kalem.gizli}
            disabled={calisiyor}
            data-testid="kutuphane-gizle"
            onChange={(e) => onGizle(!e.currentTarget.checked)}
          />
        ) : kalem.gizli ? (
          <Badge variant="light" color={ANLAM.bilgi}>
            Gizli
          </Badge>
        ) : (
          <Text size="sm" c="dimmed">
            Evet
          </Text>
        )}
        {!urunKalemi && (
          <Text size="xs" c="dimmed" data-testid="kutuphane-musteri-kalemi">
            Kendi kaleminiz
          </Text>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

/** D1 — kanıt beklentisi seçicisi; boş bırakılabilir (İ4 görünür boşluk). */
function KanitSecici({
  deger,
  devredisi,
  testId,
  onDegis,
}: {
  deger: YukumlulukKanitTipi | null;
  devredisi?: boolean;
  testId: string;
  onDegis: (deger: YukumlulukKanitTipi | null) => void;
}): React.JSX.Element {
  return (
    <Select
      size="xs"
      placeholder="Sınıflandırılmadı"
      clearable
      comboboxProps={{ withinPortal: true }}
      disabled={devredisi}
      data={(Object.keys(YUKUMLULUK_KANIT_TIPI_ETIKETI) as YukumlulukKanitTipi[]).map((tip) => ({
        value: tip,
        label: YUKUMLULUK_KANIT_TIPI_ETIKETI[tip],
      }))}
      value={deger}
      data-testid={testId}
      onChange={(v) => onDegis((v as YukumlulukKanitTipi | null) ?? null)}
    />
  );
}
